import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type Result = { label: string; status: number | string; body: unknown };

async function callFn(name: string, token: string, body: unknown = {}) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-admin-token": token,
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let parsed: unknown = text;
  try {
    parsed = JSON.parse(text);
  } catch {
    /* ignore */
  }
  return { status: res.status, body: parsed };
}

async function fetchSitemapUrls(): Promise<string[]> {
  const res = await fetch("/sitemap.xml");
  const xml = await res.text();
  const matches = [...xml.matchAll(/<loc>([^<]+)<\/loc>/g)];
  return matches.map((m) => m[1]);
}

const AdminSeo = () => {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<Result[]>([]);

  useEffect(() => {
    const saved = sessionStorage.getItem("seo_admin_token");
    if (saved) setToken(saved);
  }, []);

  useEffect(() => {
    if (token) sessionStorage.setItem("seo_admin_token", token);
  }, [token]);

  const log = (r: Result) => setResults((prev) => [r, ...prev].slice(0, 20));

  const runIndexNow = async () => {
    setBusy("indexnow");
    try {
      const urls = await fetchSitemapUrls();
      const r = await callFn("indexnow-submit", token, { urls });
      log({ label: `IndexNow (${urls.length} URLs)`, ...r });
    } catch (e) {
      log({ label: "IndexNow", status: "error", body: String(e) });
    } finally {
      setBusy(null);
    }
  };

  const runGsc = async () => {
    setBusy("gsc");
    try {
      const r = await callFn("gsc-submit-sitemap", token, {});
      log({ label: "GSC sitemap submit", ...r });
    } catch (e) {
      log({ label: "GSC", status: "error", body: String(e) });
    } finally {
      setBusy(null);
    }
  };

  const runBoth = async () => {
    await runIndexNow();
    await runGsc();
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <header>
          <h1 className="text-3xl font-bold text-primary">SEO Admin</h1>
          <p className="text-muted-foreground mt-2">
            Ping IndexNow (Bing / Yandex / Seznam / Naver) and re-submit the sitemap to Google
            Search Console.
          </p>
        </header>

        <Card className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-token">Admin token</Label>
            <Input
              id="admin-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="GSC_ADMIN_TOKEN value"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Stored in this tab's sessionStorage only.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={runIndexNow} disabled={!token || busy !== null} variant="secondary">
              {busy === "indexnow" ? "Pinging…" : "Ping IndexNow"}
            </Button>
            <Button onClick={runGsc} disabled={!token || busy !== null} variant="secondary">
              {busy === "gsc" ? "Submitting…" : "Submit sitemap to Google"}
            </Button>
            <Button onClick={runBoth} disabled={!token || busy !== null}>
              {busy ? "Working…" : "Run both"}
            </Button>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-3">Results</h2>
          {results.length === 0 ? (
            <p className="text-sm text-muted-foreground">No runs yet.</p>
          ) : (
            <ul className="space-y-3">
              {results.map((r, i) => (
                <li key={i} className="border border-border rounded-md p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{r.label}</span>
                    <span className="text-xs px-2 py-1 rounded bg-muted">{r.status}</span>
                  </div>
                  <pre className="text-xs overflow-x-auto bg-muted/40 p-2 rounded">
                    {typeof r.body === "string" ? r.body : JSON.stringify(r.body, null, 2)}
                  </pre>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminSeo;
